import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { MatCardModule } from '@angular/material/card'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { EligibilityBenefits, PatientInsurance, PatientInsuranceStatus } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { BenefitsFormGroup } from '../../services/estimate-balance-form.service'
import { BalanceBenefitRowComponent } from '../balance-benefit-row/balance-benefit-row.component'
import { BalanceBenefitsOptionsMenuComponent } from '../balance-benefits-options-menu/balance-benefits-options-menu.component'
import { BalanceInsurancePickerComponent } from '../balance-insurance-picker/balance-insurance-picker.component'

@Component({
  selector: 'mmx-balance-benefits-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCardModule,
    MatProgressBarModule,
    BalanceBenefitRowComponent,
    BalanceBenefitsOptionsMenuComponent,
    BalanceInsurancePickerComponent,
  ],
  templateUrl: './balance-benefits-panel.component.html',
  styleUrls: ['./balance-benefits-panel.component.scss'],
})
export class BalanceBenefitsPanelComponent implements OnChanges {
  readonly PatientInsuranceStatus = PatientInsuranceStatus

  @Input({ required: true }) form!: BenefitsFormGroup
  @Input() primaryLoading = false
  @Input() secondaryLoading = false
  @Input() originalPrimary: EligibilityBenefits | null = null
  @Input() originalSecondary: EligibilityBenefits | null = null
  @Input() hasSecondaryInsurance = false
  @Input() primaryInsuranceOptions: PatientInsurance[] = []
  @Input() secondaryInsuranceOptions: PatientInsurance[] = []
  @Input() primaryInsurance: PatientInsurance | null = null
  @Input() insuranceSelectionResolved = false
  @Input() secondaryInsurance: PatientInsurance | null = null
  @Input() readOnly = false
  // When embedded in the modify-session accordion the section header owns the
  // title + options menu, so the panel hides its own title row.
  @Input() embedded = false

  @Output() readonly benefitsChanged = new EventEmitter<void>()
  @Output() readonly primaryInsuranceChange = new EventEmitter<PatientInsurance>()
  @Output() readonly secondaryInsuranceChange = new EventEmitter<PatientInsurance>()

  ngOnChanges(changes: SimpleChanges): void {
    // Sync on `form` too: the form instance is shared with the draft-surface
    // panel (no readOnly binding), so re-mounting under a different panel must
    // re-assert enabled state even when readOnly hasn't changed.
    if ((changes['readOnly'] || changes['form']) && this.form) {
      if (this.readOnly) {
        this.form.disable({ emitEvent: false })
      } else {
        this.form.enable({ emitEvent: false })
      }
    }
  }

  get loading(): boolean {
    return this.primaryLoading || this.secondaryLoading
  }

  get hasSecondary(): boolean {
    return this.form?.controls.hasSecondary.value === true
  }

  onCommitted(): void {
    this.benefitsChanged.emit()
  }
}
